import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Server, PhoneCall, Grid3x3, Phone, MousePointer2, Mic, Pause,
  ArrowRightLeft, Circle, Check, X, ShieldCheck, Lock, UserCog, Clock,
} from "lucide-react";

const AC = "#0047FF";
const useStep = (n, ms) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((v) => (v + 1) % n), ms);
    return () => clearInterval(id);
  }, [n, ms]);
  return i;
};

const Frame = ({ children, caption, testid }) => (
  <motion.div
    whileHover={{ y: -6, borderColor: "rgba(0,71,255,0.6)", boxShadow: "0 30px 70px -20px rgba(0,71,255,0.35)" }}
    transition={{ type: "spring", stiffness: 300, damping: 24 }}
    className="bg-[#0a0a0a] text-white border border-black shadow-[0_20px_50px_-20px_rgba(0,0,0,0.4)]"
    data-testid={testid}
  >
    <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
      <span className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-400 flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[#0047FF] animate-pulse" /> {caption}
      </span>
    </div>
    <div className="p-5 min-h-[190px] flex items-center justify-center">{children}</div>
  </motion.div>
);

const Isolate = () => {
  const a = useStep(6, 850);
  return (
    <div className="grid grid-cols-3 gap-2.5 w-full">
      {Array.from({ length: 6 }).map((_, i) => (
        <motion.div
          key={i}
          animate={{ borderColor: i === a ? AC : "rgba(255,255,255,0.12)", backgroundColor: i === a ? "rgba(0,71,255,0.12)" : "rgba(255,255,255,0.02)" }}
          className="h-14 border grid place-items-center"
        >
          <Building2 size={16} className={i === a ? "text-[#0047FF]" : "text-neutral-600"} />
        </motion.div>
      ))}
    </div>
  );
};

const Toggles = ({ labels = ["Extensions", "Routing", "Recording"] }) => {
  const a = useStep(labels.length, 900);
  return (
    <div className="w-full space-y-3">
      {labels.map((l, i) => (
        <div key={l} className="flex items-center justify-between">
          <span className="text-sm text-neutral-300">{l}</span>
          <motion.div animate={{ backgroundColor: i <= a ? AC : "rgba(255,255,255,0.15)" }} className="w-10 h-5 rounded-full p-0.5 flex">
            <motion.span layout className="w-4 h-4 bg-white rounded-full" animate={{ x: i <= a ? 20 : 0 }} transition={{ type: "spring", stiffness: 400, damping: 25 }} />
          </motion.div>
        </div>
      ))}
    </div>
  );
};

const Console = ({ caption }) => {
  const a = useStep(5, 700);
  return (
    <div className="w-full space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <motion.div key={i} animate={{ backgroundColor: i === a ? "rgba(0,71,255,0.12)" : "rgba(255,255,255,0.03)", borderColor: i === a ? AC : "rgba(255,255,255,0)" }} className="h-7 border-l-2 flex items-center gap-2 px-3">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: i === a ? AC : "#444" }} />
          <span className="h-1.5 bg-white/20" style={{ width: `${40 + (i * 12) % 45}%` }} />
        </motion.div>
      ))}
    </div>
  );
};

const Connect = () => {
  const carriers = ["Airtel", "Tata", "Twilio"];
  const a = useStep(carriers.length, 1400);
  return (
    <div className="w-full flex items-center justify-between gap-2">
      <div className="grid place-items-center gap-1 text-center">
        <span className="grid place-items-center w-12 h-12 bg-[#0047FF] text-white"><Server size={18} /></span>
        <span className="font-mono-t text-[9px] text-neutral-400">OwnPBX</span>
      </div>
      <div className="flex-1 relative h-px bg-white/15 mx-1">
        <motion.span className="absolute -top-1 w-2 h-2 rounded-full" style={{ background: AC }} animate={{ left: ["0%", "100%"] }} transition={{ duration: 1.3, repeat: Infinity, ease: "linear" }} />
      </div>
      <div className="grid place-items-center gap-1 text-center">
        <span className="grid place-items-center w-12 h-12 border border-white/15"><PhoneCall size={18} className="text-neutral-300" /></span>
        <AnimatePresence mode="wait">
          <motion.span key={a} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="font-mono-t text-[9px] text-neutral-400">{carriers[a]}</motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
};

const Rebrand = () => {
  const brands = [{ n: "OwnPBX", c: AC }, { n: "NovaVoice", c: "#ffffff" }, { n: "Agency.io", c: AC }];
  const a = useStep(brands.length, 1500);
  return (
    <div className="w-full border border-white/12">
      <motion.div className="h-1.5" animate={{ backgroundColor: brands[a].c }} />
      <div className="p-4">
        <AnimatePresence mode="wait">
          <motion.p key={a} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="font-heading text-lg" style={{ color: brands[a].c === "#ffffff" ? "#fff" : AC }}>{brands[a].n}</motion.p>
        </AnimatePresence>
        <div className="mt-3 space-y-1.5">
          <span className="block h-1.5 w-3/4 bg-white/15" /><span className="block h-1.5 w-1/2 bg-white/10" />
        </div>
      </div>
    </div>
  );
};

const Scale = () => {
  const vals = ["5", "50", "500", "5,000"];
  const a = useStep(vals.length, 1000);
  return (
    <div className="w-full flex items-end justify-between gap-3">
      <div className="flex items-end gap-1.5 h-24 flex-1">
        {Array.from({ length: 7 }).map((_, i) => (
          <motion.span key={i} className="flex-1" style={{ background: i <= a * 2 ? AC : "rgba(255,255,255,0.15)" }} animate={{ height: `${20 + i * 11}%` }} transition={{ duration: 0.5 }} />
        ))}
      </div>
      <div className="text-right">
        <AnimatePresence mode="wait">
          <motion.p key={a} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="font-heading text-3xl text-white">{vals[a]}</motion.p>
        </AnimatePresence>
        <p className="font-mono-t text-[9px] uppercase tracking-widest text-neutral-500">extensions</p>
      </div>
    </div>
  );
};

const NumberList = ({ items }) => {
  const [c, setC] = useState(1);
  useEffect(() => { const id = setInterval(() => setC((v) => (v >= items.length ? 1 : v + 1)), 900); return () => clearInterval(id); }, [items.length]);
  return (
    <div className="w-full space-y-2 font-mono-t text-[12px]">
      <AnimatePresence initial={false}>
        {items.slice(0, c).map((n, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className="flex items-center justify-between border-b border-white/8 pb-1.5">
            <span className="text-neutral-200">{n}</span>
            <span className="text-[9px] uppercase tracking-widest" style={{ color: AC }}>provisioned</span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

const Ivr = () => {
  const opts = ["1 · Sales", "2 · Support", "3 · Billing"];
  const a = useStep(opts.length, 1000);
  return (
    <div className="w-full">
      <div className="text-center mb-3"><span className="inline-block px-3 py-1.5 border border-white/15 font-mono-t text-[11px]">Main menu</span></div>
      <div className="flex justify-center"><span className="h-4 w-px bg-white/15" /></div>
      <div className="grid grid-cols-3 gap-2">
        {opts.map((o, i) => (
          <motion.div key={o} animate={{ borderColor: i === a ? AC : "rgba(255,255,255,0.12)", color: i === a ? "#fff" : "#888" }} className="border py-2.5 text-center font-mono-t text-[11px]">{o}</motion.div>
        ))}
      </div>
    </div>
  );
};

const Queue = () => {
  const a = useStep(3, 900);
  return (
    <div className="w-full">
      <div className="flex items-center gap-2 mb-4 font-mono-t text-[11px] text-neutral-300">
        <PhoneCall size={13} style={{ color: AC }} /> Incoming call
        <motion.span className="ml-auto text-[9px]" style={{ color: AC }} animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1, repeat: Infinity }}>routing…</motion.span>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {["Priya", "Arjun", "Meera"].map((n, i) => (
          <motion.div key={n} animate={{ borderColor: i === a ? AC : "rgba(255,255,255,0.12)", backgroundColor: i === a ? "rgba(0,71,255,0.12)" : "rgba(255,255,255,0)" }} className="border p-2.5 text-center">
            <span className="grid place-items-center w-8 h-8 mx-auto rounded-full mb-1.5" style={{ background: i === a ? AC : "rgba(255,255,255,0.08)" }}>
              <Circle size={10} className="text-white" fill="currentColor" />
            </span>
            <span className="font-mono-t text-[10px] text-neutral-300">{n}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const Recording = () => {
  const [t, setT] = useState(0);
  useEffect(() => { const id = setInterval(() => setT((v) => v + 1), 1000); return () => clearInterval(id); }, []);
  const mmss = `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
  return (
    <div className="w-full">
      <div className="flex items-center gap-2 mb-4">
        <motion.span className="w-3 h-3 rounded-full bg-[#0047FF]" animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1, repeat: Infinity }} />
        <span className="font-mono-t text-[11px] uppercase tracking-widest text-neutral-300">REC · {mmss}</span>
      </div>
      <div className="flex items-center gap-[3px] h-14">
        {Array.from({ length: 40 }).map((_, i) => (
          <motion.span key={i} className="flex-1 bg-white/60 origin-center" animate={{ scaleY: [0.2, (i % 5) * 0.18 + 0.35, 0.25] }} transition={{ duration: 1 + (i % 4) * 0.15, repeat: Infinity, delay: (i % 6) * 0.05 }} style={{ height: "100%" }} />
        ))}
      </div>
    </div>
  );
};

const Hours = () => {
  const states = [{ t: "Open · 9:00–18:00", on: true }, { t: "After hours · voicemail", on: false }];
  const a = useStep(states.length, 1800);
  return (
    <div className="w-full flex flex-col items-center gap-4">
      <Clock size={28} style={{ color: states[a].on ? AC : "#666" }} />
      <AnimatePresence mode="wait">
        <motion.div key={a} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: states[a].on ? AC : "#666" }} />
          <span className="font-mono-t text-[12px] text-neutral-200">{states[a].t}</span>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

const keypad = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];
const Dialpad = () => {
  const seq = "80471";
  const [typed, setTyped] = useState("");
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => { i = i >= seq.length ? 0 : i + 1; setTyped(seq.slice(0, i)); }, 500);
    return () => clearInterval(id);
  }, []);
  const active = typed.slice(-1);
  return (
    <div className="w-full">
      <p className="text-center font-mono-t text-lg tracking-widest mb-3 min-h-[24px]">{typed || <span className="text-neutral-600">+91…</span>}</p>
      <div className="grid grid-cols-3 gap-1.5">
        {keypad.map((k) => (
          <motion.div key={k} animate={{ backgroundColor: active === k ? AC : "rgba(255,255,255,0.04)" }} className="relative grid place-items-center py-2 font-heading text-sm border border-white/10">
            {k}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Click-to-call is one click on an existing contact, not digit-by-digit
// keying — the old demo reused the Dialpad component, which showed the
// opposite of what the feature actually does.
const clickToCallContacts = ["Priya Sharma · +91 98765 43210", "Arjun Mehta · +91 91234 56789", "Support line · +91 80 4718 2210"];
const ClickToCall = () => {
  const a = useStep(clickToCallContacts.length, 1400);
  return (
    <div className="w-full space-y-2 font-mono-t text-[12px]">
      {clickToCallContacts.map((c, i) => {
        const isActive = i === a;
        return (
          <motion.div
            key={c}
            animate={{ borderColor: isActive ? AC : "rgba(255,255,255,0.1)" }}
            className="relative flex items-center justify-between border px-3 py-2.5"
          >
            <span className={isActive ? "text-white" : "text-neutral-400"}>{c}</span>
            <AnimatePresence mode="wait">
              {isActive ? (
                <motion.span
                  key="calling"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest"
                  style={{ color: AC }}
                >
                  <PhoneCall size={12} /> Calling…
                </motion.span>
              ) : (
                <motion.span key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-neutral-600">
                  <Phone size={12} />
                </motion.span>
              )}
            </AnimatePresence>
            <AnimatePresence>
              {isActive && (
                <motion.div
                  initial={{ opacity: 0, x: 14, y: -10 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute -right-1.5 -top-1.5"
                >
                  <MousePointer2 size={14} className="text-white" fill="currentColor" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

const Controls = () => {
  const ctrls = [{ i: Mic, l: "Mute" }, { i: Pause, l: "Hold" }, { i: ArrowRightLeft, l: "Transfer" }, { i: PhoneCall, l: "Record" }];
  const a = useStep(ctrls.length, 900);
  return (
    <div className="w-full grid grid-cols-4 gap-2">
      {ctrls.map((c, i) => (
        <motion.div key={c.l} animate={{ backgroundColor: i === a ? AC : "rgba(255,255,255,0.04)", borderColor: i === a ? AC : "rgba(255,255,255,0.12)" }} className="border py-3 flex flex-col items-center gap-1.5">
          <c.i size={15} className="text-white" />
          <span className="font-mono-t text-[9px] text-neutral-300">{c.l}</span>
        </motion.div>
      ))}
    </div>
  );
};

const Presence = () => {
  const a = useStep(3, 1100);
  const statuses = ["Available", "On call", "Away"];
  const cols = [AC, "#ffffff", "#666"];
  return (
    <div className="w-full space-y-2.5">
      {["Priya", "Arjun", "Meera"].map((n, i) => {
        const s = (a + i) % 3;
        return (
          <div key={n} className="flex items-center justify-between border border-white/10 px-3 py-2.5">
            <span className="flex items-center gap-2.5 text-sm text-neutral-200"><span className="grid place-items-center w-6 h-6 rounded-full bg-white/10 font-mono-t text-[10px]">{n[0]}</span>{n}</span>
            <span className="flex items-center gap-1.5 font-mono-t text-[10px] text-neutral-400"><motion.span className="w-2 h-2 rounded-full" animate={{ backgroundColor: cols[s] }} />{statuses[s]}</span>
          </div>
        );
      })}
    </div>
  );
};

const Dashboard = () => {
  const [n, setN] = useState({ calls: 12, queue: 3, avg: 41 });
  useEffect(() => {
    const id = setInterval(() => setN({ calls: 8 + Math.floor(Math.random() * 14), queue: Math.floor(Math.random() * 6), avg: 30 + Math.floor(Math.random() * 30) }), 1100);
    return () => clearInterval(id);
  }, []);
  const tiles = [{ k: "Active calls", v: n.calls }, { k: "In queue", v: n.queue }, { k: "Avg wait (s)", v: n.avg }];
  return (
    <div className="w-full grid grid-cols-3 gap-2">
      {tiles.map((t) => (
        <div key={t.k} className="border border-white/10 p-3">
          <AnimatePresence mode="wait">
            <motion.p key={t.v} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="font-heading text-2xl" style={{ color: AC }}>{t.v}</motion.p>
          </AnimatePresence>
          <p className="font-mono-t text-[9px] uppercase tracking-widest text-neutral-500 mt-1">{t.k}</p>
        </div>
      ))}
    </div>
  );
};

const whitelistRows = [{ ip: "203.0.113.24", ok: true }, { ip: "45.148.10.91", ok: false }, { ip: "198.51.100.7", ok: true }];
const Whitelist = () => {
  const rows = whitelistRows;
  const [c, setC] = useState(1);
  useEffect(() => { const id = setInterval(() => setC((v) => (v >= whitelistRows.length ? 1 : v + 1)), 1100); return () => clearInterval(id); }, []);
  return (
    <div className="w-full space-y-2 font-mono-t text-[12px]">
      <AnimatePresence initial={false}>
        {rows.slice(0, c).map((r, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className="flex items-center justify-between border border-white/10 px-3 py-2">
            <span className="flex items-center gap-2.5">
              <span className="grid place-items-center w-5 h-5" style={{ background: r.ok ? AC : "rgba(255,255,255,0.1)" }}>{r.ok ? <Check size={12} /> : <X size={12} />}</span>
              <span className={r.ok ? "text-neutral-200" : "text-neutral-500 line-through"}>{r.ip}</span>
            </span>
            <span className="text-[9px] uppercase tracking-widest" style={{ color: r.ok ? AC : "#666" }}>{r.ok ? "Allowed" : "Blocked"}</span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

const LockDemo = () => {
  const a = useStep(4, 700);
  const blocked = a === 3;
  return (
    <div className="w-full flex flex-col items-center gap-4">
      <motion.span animate={blocked ? { x: [0, -5, 5, -3, 0] } : {}} transition={{ duration: 0.4 }} className="grid place-items-center w-14 h-14 border" style={{ borderColor: blocked ? "#666" : AC }}>
        <Lock size={22} style={{ color: blocked ? "#666" : AC }} />
      </motion.span>
      <div className="flex gap-2">
        {[0, 1, 2, 3].map((i) => (
          <motion.span key={i} className="w-3 h-3 rounded-full" animate={{ backgroundColor: i <= a && !blocked ? AC : "rgba(255,255,255,0.15)" }} />
        ))}
      </div>
      <span className="font-mono-t text-[10px] uppercase tracking-widest" style={{ color: blocked ? "#888" : AC }}>{blocked ? "Attempt blocked" : "Authenticating"}</span>
    </div>
  );
};

const Roles = () => {
  const roles = [{ r: "Admin", p: "Full access" }, { r: "Agent", p: "Calls only" }, { r: "Viewer", p: "Read-only" }];
  const a = useStep(roles.length, 1100);
  return (
    <div className="w-full space-y-2.5">
      {roles.map((x, i) => (
        <motion.div key={x.r} animate={{ borderColor: i === a ? AC : "rgba(255,255,255,0.12)", backgroundColor: i === a ? "rgba(0,71,255,0.1)" : "rgba(255,255,255,0)" }} className="border flex items-center justify-between px-3 py-2.5">
          <span className="flex items-center gap-2 text-sm text-neutral-200"><UserCog size={14} style={{ color: i === a ? AC : "#888" }} />{x.r}</span>
          <span className="font-mono-t text-[10px] text-neutral-400">{x.p}</span>
        </motion.div>
      ))}
    </div>
  );
};

const Rack = () => (
  <div className="w-full flex items-center gap-4">
    <ShieldCheck size={30} style={{ color: AC }} className="shrink-0" />
    <div className="flex-1 space-y-1.5">
      {Array.from({ length: 4 }).map((_, r) => (
        <div key={r} className="flex items-center gap-1.5 border border-white/10 px-2 py-1.5">
          <span className="h-2 flex-1 bg-white/10" />
          {Array.from({ length: 4 }).map((_, d) => (
            <motion.span key={d} className="w-1.5 h-1.5 rounded-full" animate={{ backgroundColor: [AC, "rgba(255,255,255,0.15)", AC] }} transition={{ duration: 1.4, repeat: Infinity, delay: (r + d) * 0.12 }} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

const registry = {
  isolate: { c: <Isolate />, cap: "Tenants" },
  toggles: { c: <Toggles />, cap: "Config" },
  "toggles-rec": { c: <Toggles labels={["Auto-record", "Retention 90d", "Access control"]} />, cap: "Recording policy" },
  console: { c: <Console />, cap: "Control plane" },
  connect: { c: <Connect />, cap: "Carrier link" },
  rebrand: { c: <Rebrand />, cap: "White-label" },
  scale: { c: <Scale />, cap: "Scale" },
  extensions: { c: <NumberList items={["Ext. 101 · Sales", "Ext. 104 · Priya", "Ext. 233 · Support", "Trunk · SIP-A"]} />, cap: "Extensions & trunks" },
  did: { c: <NumberList items={["+91 80 4718 2210", "+91 22 6150 9900", "+1 415 555 0132", "+44 20 7946 0991"]} />, cap: "DID pool" },
  ivr: { c: <Ivr />, cap: "IVR menu" },
  queue: { c: <Queue />, cap: "Queue routing" },
  recording: { c: <Recording />, cap: "Recording" },
  hours: { c: <Hours />, cap: "Business hours" },
  softphone: { c: <Dialpad />, cap: "Softphone" },
  clicktocall: { c: <ClickToCall />, cap: "Click-to-call" },
  controls: { c: <Controls />, cap: "Call controls" },
  presence: { c: <Presence />, cap: "Presence" },
  dashboard: { c: <Dashboard />, cap: "Live dashboard" },
  whitelist: { c: <Whitelist />, cap: "IP allowlist" },
  lock: { c: <LockDemo />, cap: "Login guard" },
  roles: { c: <Roles />, cap: "Roles" },
  rack: { c: <Rack />, cap: "Owned infra" },
};

export const FeatureDemo = ({ kind }) => {
  const item = registry[kind];
  if (!item) return null;
  return <Frame caption={item.cap} testid={`feature-demo-${kind}`}>{item.c}</Frame>;
};
